<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="visible"
        class="custom-modal-overlay"
        @click="handleOverlayClick"
      >
        <div class="custom-modal-container" @click.stop>
          <button
            v-if="closeButton"
            class="custom-modal-close"
            type="button"
            @click="handleClose"
          >
            <svg
              class="h-3 w-3"
              viewBox="0 0 14 14"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
            >
              <path
                clip-rule="evenodd"
                d="M8.41155 7.00032L13.6886 12.2822C14.079 12.6729 14.0787 13.306 13.688 13.6964C13.2973 14.0867 12.6641 14.0864 12.2738 13.6957L6.99628 8.41348L1.70658 13.6953C1.31577 14.0856 0.682602 14.0851 0.292368 13.6943C-0.0978661 13.3035 -0.0973954 12.6703 0.293419 12.2801L5.58271 6.99863L0.29565 1.70679C-0.094698 1.31609 -0.0944116 0.682921 0.296289 0.292573C0.68699 -0.0977741 1.32016 -0.0974878 1.7105 0.293213L6.99797 5.58547L12.2739 0.317343C12.6648 -0.0728905 13.2979 -0.0724199 13.6881 0.318395C14.0784 0.709209 14.0779 1.34237 13.6871 1.73261L8.41155 7.00032Z"
              />
            </svg>
          </button>

          <slot></slot>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { watch, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  closeButton: {
    type: Boolean,
    default: true,
  },
  closeOutside: {
    type: Boolean,
    default: true,
  },
})

const emits = defineEmits(['close'])

function handleClose() {
  emits('close')
}

function handleOverlayClick() {
  if (props.closeOutside) {
    handleClose()
  }
}

function handleBodyScroll() {
  if (props.visible) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

watch(() => props.visible, handleBodyScroll)

onMounted(() => {
  if (props.visible) {
    handleBodyScroll()
  }
})

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<style scoped>
.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  overflow: hidden;
}

.custom-modal-container {
  background: var(--bg-2, #ffffff);
  border: 1px solid var(--bg-3, #e5e7eb);
  border-radius: 10px;
  width: min(600px, 100%);
  max-height: calc(100vh - 40px);
  padding: 30px;
  position: relative;
  overflow-y: auto;
  box-shadow: 0 0 72px rgba(3, 1, 0, 0.08);
}

.custom-modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-1, #374151);
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s ease;
  z-index: 1;
}

.custom-modal-close:hover {
  color: var(--brand-hover, #2563eb);
}

.custom-modal-close:focus {
  outline: none;
  color: var(--brand-hover, #2563eb);
}

.custom-modal-close svg {
  width: 12px;
  height: 12px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .custom-modal-container,
.modal-fade-leave-active .custom-modal-container {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.modal-fade-enter-from .custom-modal-container,
.modal-fade-leave-to .custom-modal-container {
  transform: translateY(-20px) scale(0.95);
  opacity: 0;
}

@media (max-width: 768px) {
  .custom-modal-overlay {
    padding: 10px;
  }

  .custom-modal-container {
    width: 100%;
    max-height: calc(100vh - 20px);
    padding: 20px;
  }

  .custom-modal-close {
    top: 15px;
    right: 15px;
  }
}
</style>
